import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { wrapService } from 'actions/service_wrapper.actions';
import { putInsuranceUserPrefill } from 'externals/_services/product.service';
import { fetchApplyNowTracking } from 'externals/_services/tracking.service';
import { useDispatch } from 'react-redux';
import { useLogger } from 'hook/error.hook';
import { useComplexState } from 'hook/helper.hook';
import {
  plantMetadataInLocalStorageAndReturnTransferUrl,
  convertProps,
  setPartnerNameAndImageWithId,
} from 'partial/OfferManager/holder/genericOfferLogic';
import { getFullAttributes } from 'helpers/tracking.helpers';
import { getMrphPageFormFactor } from 'helpers/metadataLogic.helpers';
import { CLICK_APPLY } from 'externals/_tracking/types/eventTypes.constants';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import usePhoneNumberInput from 'pages/Profile/pages/ProfileOverview/hooks/usePhoneNumberInput';
import { useAddressForm } from 'hook/addressForm.hook';
import { formatPhoneWithDashes } from 'helpers/phone.helper';
import { INSURANCE_PRE_FILL } from 'types/mrph.pageName.constants';
import { AUTO_INSURANCE_VERTICAL } from 'types/marketplaceTiles.constants';

const a_putInsuranceUserPrefill = wrapService(putInsuranceUserPrefill, {
  name: 'POST_INSURANCE_PREFILL',
});

const a_fetchApplyNowTracking = wrapService(fetchApplyNowTracking, {
  name: 'ai_fetchApplyNowTracking',
});

//TODO move clickapply and update functions to a holder , in order to build this we need details API from slapi , svc tickets waiting for approval.
export const useInsurancePrefill = (userInfo, eventHandler) => {
  const logger = useLogger({
    name: 'insurancePrefill.hook',
  });
  const { ocfLoggedIn } = useSelector(state => ({
    ocfLoggedIn: state.app.ocfLoggedIn,
  }));

  const [statesPrefill, setStatesPrefill] = useComplexState({
    isEditPhone: false,
    isEditAddress: false,
    loadingUserPrefill: false,
    clickUrl: null,
    partnerClickUrl: null,
    loadingPartnerPage: false,
    offerMetadata: null,
    showUserInfoForm: false,
  });

  const addressForm = useAddressForm(userInfo?.primaryAddress, true);
  const phoneNumberInput = usePhoneNumberInput(
    userInfo?.contactPhoneNumber,
    true,
  );

  const dispatch = useDispatch();
  const onCancelUpdatePrefill = () => {
    defaultEventHandler(CLICK, {
      'Click Type': 'Cancel update review information',
      Vertical: AUTO_INSURANCE_VERTICAL,
    });
    setStatesPrefill({
      isEditPhone: false,
      isEditAddress: false,
    });
    addressForm.updateAddress(userInfo?.primaryAddress);
    addressForm.clearError();
    phoneNumberInput.update(userInfo?.contactPhoneNumber);
  };

  useEffect(() => {
    addressForm.isAddressValid({
      street: addressForm.street,
      zip: addressForm.zip,
      city: addressForm.city,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressForm.street, addressForm.zip, addressForm.city]);

  const updatePrefillUserInfo = () => {
    if (
      !addressForm.streetError &&
      !addressForm.zipError &&
      !addressForm.cityError &&
      !phoneNumberInput.contactPhoneNumber.error
    ) {
      const userPrefillInfo = {
        phone: formatPhoneWithDashes(phoneNumberInput.contactPhoneNumber.value),
        address: addressForm.getFullAddress(),
        clickUrl: statesPrefill.partnerClickUrl,
        insuranceType: 'AUTO',
        companyName: statesPrefill.offerMetadata.displayName,
      };
      setStatesPrefill({
        loadingUserPrefill: true,
      });

      postInsurancePrefill(userPrefillInfo)
        .then(response => {
          defaultEventHandler(CLICK, {
            'Click Type': 'Update review information',
            Vertical: AUTO_INSURANCE_VERTICAL,
          });
          setStatesPrefill({
            clickUrl: response.clickUrl,
            partnerClickUrl: response.partnerClickUrl,
            loadingUserPrefill: false,
            isEditPhone: false,
            isEditAddress: false,
          });
        })
        .catch(err => {
          logger.reportAPIError(err);
          setStatesPrefill({
            loadingUserPrefill: false,
          });
        });
    }
  };

  const postInsurancePrefill = userPrefillInfo => {
    return dispatch(a_putInsuranceUserPrefill(userPrefillInfo));
  };

  const defaultEventHandler = (name, attributes = {}) => {
    const fullAttributes = getFullAttributes({
      partner: statesPrefill.offerMetadata.displayName,
      pagePosition: 'Marketplace Auto Insurance',
      modulePosition: 1,
      offerMetadata: statesPrefill.offerMetadata,
      attributes: attributes,
      isModal: false,
    });
    eventHandler(name, fullAttributes);
  };

  const onApplyClick = (e, showPrefill) => {
    if (statesPrefill.loadingPartnerPage) return;
    setStatesPrefill({
      loadingPartnerPage: true,
    });
    e.preventDefault();
    const id = setPartnerNameAndImageWithId(
      statesPrefill.offerMetadata.displayName,
      statesPrefill.offerMetadata.imageLink,
    );

    defaultEventHandler(CLICK, {
      'Click Type': showPrefill
        ? 'Continue with pre-filling'
        : 'Continue without pre-filling',
      Vertical: AUTO_INSURANCE_VERTICAL,
    });

    const propsForBackend = convertProps(
      getFullAttributes({
        partner: statesPrefill.offerMetadata.displayName,
        pagePosition: 'Marketplace Auto Insurance',
        modulePosition: 1,
        offerMetadata: statesPrefill.offerMetadata,
        attributes: {
          'Click Type': 'Apply',
          'Page Name': INSURANCE_PRE_FILL,
          'Page Form Factor': getMrphPageFormFactor(),
          Vertical: AUTO_INSURANCE_VERTICAL,
        },
        isModal: false,
        includeBackendRefCode: true,
      }),
    );

    let windowReference = window.open();

    dispatch(
      a_fetchApplyNowTracking(statesPrefill.clickUrl, {
        props: propsForBackend,
        prefill: showPrefill,
      }),
    )
      .then(trackingMetadata => {
        defaultEventHandler(CLICK_APPLY, {
          'Click Type': 'Apply',
          'Click ID': trackingMetadata.clickId,
          'Page Form Factor': getMrphPageFormFactor(),
          Partner: statesPrefill.offerMetadata.displayName,
          Vertical: AUTO_INSURANCE_VERTICAL,
        });

        const transferUrl = plantMetadataInLocalStorageAndReturnTransferUrl(
          trackingMetadata.partnerUrl,
          id,
          null,
          ocfLoggedIn,
        );

        windowReference.location = transferUrl;
        setStatesPrefill({
          loadingPartnerPage: false,
        });
      })
      .catch(err => {
        logger.showError('A problem happened while Transferring', err.message);
        logger.reportAPIError(err);
        windowReference.close();
        setStatesPrefill({
          loadingPartnerPage: false,
        });
      });
  };

  return {
    isEdit: statesPrefill.isEditPhone || statesPrefill.isEditAddress,
    statesPrefill,
    setStatesPrefill,
    addressForm,
    phoneNumberInput,
    onCancelUpdatePrefill,
    updatePrefillUserInfo,
    onApplyClick,
  };
};
